/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'clsx';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  OutlinedInput,
} from '@mui/material';

import { HUModal2, HUModal2Props } from '../../../../components/HUModal2/HUModal2';
import { HUSpin } from '../../../../components/HUSpin/HUSpin';
import { SimpleErrorModal } from '../../../../components/SimpleErrorModal/SimpleErrorModal';
import { Namespaces } from '../../../../i18n';
import { SUPlanApiService } from '../../../../services/SUPlanApiService';
import { useFormStyles } from '../../../../styles/form';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { AdminPlanCompute } from '../../../../types/su-plan/entities/AdminPlanCompute';
import { PlanComputeUpdateRequest } from '../../../../types/su-plan/requests/PlanComputeUpdateRequest';
import { useApiValidation } from '../../../../utils/useApiValidation';
import { useObjectState } from '../../../../utils/useObjectState';
import { useValidationTranslation } from '../../../../utils/useValidationTranslation';
import {
  formInitialValues,
  UpdatePlanComputeDialogValues,
  validationSchema,
} from './UpdatePlanComputeDialog.miscellaneous';

export type UpdatePlanComputeDialogOnSuccessCallback = (params: {
  payload: PlanComputeUpdateRequest;
  planToEdit: AdminPlanCompute;
}) => any;

type UpdatePlanComputeDialogProps = Pick<HUModal2Props, 'open'> &
  Required<Pick<HUModal2Props, 'onCancel'>> & {
    className?: string;
    onSuccess: UpdatePlanComputeDialogOnSuccessCallback;
    planToEdit: AdminPlanCompute | undefined;
  };

export function UpdatePlanComputeDialog(props: UpdatePlanComputeDialogProps) {
  const { className, open, onCancel, onSuccess, planToEdit } = props;
  const formClasses = useFormStyles();

  const { t } = useTranslation([Namespaces.Admin]);
  const { vtr } = useValidationTranslation();

  const [errorModal, setErrorModal] = useObjectState<{
    isOpen: boolean;
    error: string;
  }>({ isOpen: false, error: '' });

  const initialValues = useMemo<UpdatePlanComputeDialogValues>(
    () => ({
      ...formInitialValues,
      name: planToEdit?.name ?? '',
      isVisible: planToEdit?.isVisible ?? false,
      isDefault: planToEdit?.isDefault ?? false,
    }),
    [planToEdit],
  );

  const { setApiError, getFieldApiError, resetFieldApiError } = useApiValidation();

  const [updateComputePlanMutation, { isLoading }] = useMutation(SUPlanApiService.updatePlanCompute, {
    onSuccess(data) {
      if (planToEdit) {
        const {
          data: { payload },
        } = data;
        onSuccess({ payload, planToEdit });
      }
    },
    onError: (e: ApiClientError) => {
      const validation = setApiError(e);
      if (validation.errorType === 'unexpected' || validation.errorType === 'common') {
        setErrorModal({
          isOpen: true,
          error: `manage_plans.dialogs.errors.${validation.error ?? ''}`,
        });
      }
    },
  });

  const onFormSubmit = useCallback(
    async (values: UpdatePlanComputeDialogValues) => {
      if (!planToEdit) {
        return;
      }
      const data: PlanComputeUpdateRequest = {
        ...values,
      };
      await updateComputePlanMutation({
        data,
        planId: planToEdit.id,
      });
    },
    [updateComputePlanMutation, planToEdit],
  );

  const formikProps = useFormik<UpdatePlanComputeDialogValues>({
    validationSchema,
    enableReinitialize: true,
    initialValues,
    onSubmit: onFormSubmit,
  });

  const { values, touched, errors, handleBlur, handleChange, handleSubmit, submitForm, resetForm } = formikProps;

  useEffect(() => {
    if (!open) {
      resetForm({ values: { ...formInitialValues } });
    }
  }, [resetForm, open]);

  const getError = (field: keyof UpdatePlanComputeDialogValues): string => {
    return errors[field] || getFieldApiError(field) || '';
  };

  const hasError = (field: keyof UpdatePlanComputeDialogValues): boolean => {
    return !!touched[field] && !!getError(field);
  };

  const onTextFieldChange = (name: keyof UpdatePlanComputeDialogValues, event: React.ChangeEvent) => {
    handleChange(event);
    resetFieldApiError(name);
  };

  const getLabelKey = (key: string): string => {
    return `manage_plans.dialogs.labels.${key}`;
  };

  const handleOkClick = () => setErrorModal({ isOpen: false });

  return (
    <HUModal2
      open={open}
      okButtonText={t('manage_plans.dialogs.buttons.submit')}
      okButtonProps={{ disabled: !formikProps.dirty || isLoading }}
      cancelButtonText={t('manage_plans.dialogs.buttons.cancel')}
      onCancel={onCancel}
      onClose={onCancel}
      disableRestoreFocus
      onOk={submitForm}
      size="big"
      title={t('manage_plans.dialogs.titles.compute_plan.update')}
    >
      <HUSpin spinning={isLoading} delay={50}>
        <form noValidate className={cx(className)} onSubmit={handleSubmit}>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('name')}
            variant="outlined"
          >
            <InputLabel htmlFor="name">{t(getLabelKey('name'))}</InputLabel>
            <OutlinedInput
              id="name"
              name="name"
              label={t(getLabelKey('name'))}
              error={hasError('name')}
              fullWidth
              type="text"
              value={values.name}
              onChange={onTextFieldChange.bind(null, 'name')}
              disabled={isLoading}
              onBlur={handleBlur}
            />
            {hasError('name') && (
              <FormHelperText>
                {vtr(getError('name'), {
                  field: t(getLabelKey('name')),
                })}
              </FormHelperText>
            )}
          </FormControl>
          <div className={formClasses.checkboxes}>
            <FormControlLabel
              name="isVisible"
              checked={values.isVisible}
              onChange={handleChange}
              control={<Checkbox color="primary" />}
              label={t(getLabelKey('is_visible'))}
              labelPlacement="end"
              disabled={isLoading}
            />
            <FormControlLabel
              name="isDefault"
              checked={values.isDefault}
              onChange={handleChange}
              control={<Checkbox color="primary" />}
              label={t(getLabelKey('is_default'))}
              labelPlacement="end"
              disabled={isLoading}
            />
          </div>
          <div className={formClasses.hiddenSubmitBtn}>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              size="medium"
              disabled={isLoading || !formikProps.dirty}
              aria-label={t('manage_plans.dialogs.buttons.submit')}
            >
              &nbsp;
            </Button>
          </div>
          <SimpleErrorModal open={errorModal.isOpen} message={errorModal.error} t={t} onOk={handleOkClick} />
        </form>
      </HUSpin>
    </HUModal2>
  );
}
